import { useState, useEffect } from 'react'
import {
  Dialog, DialogContent, DialogTitle,
  TextField, Typography, Divider, Box,
} from '@mui/material'
import { FancyButton } from '../../comps/FancyButtons'

interface InputModalProps {
  open: boolean
  title: string
  okLabel?: string
  cancelLabel?: string
  placeholder?: string
  label?: string
  initialValue?: string
  singleLine?: boolean
  // Optional prefix so callers can disambiguate; defaults to "inputmodal".
  testIdPrefix?: string
  // Return a non-empty string to block the submit and show it as an inline error.
  validate?: (value: string) => string | null | undefined
  onConfirm: (value: string) => void
  onClose: () => void
}

export default function InputModal({
  open,
  title,
  okLabel = 'OK',
  cancelLabel = 'Cancel',
  placeholder = '',
  label,
  initialValue = '',
  singleLine = false,
  testIdPrefix = 'inputmodal',
  validate,
  onConfirm,
  onClose,
}: InputModalProps) {
  const [value, setValue] = useState(initialValue)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) { setValue(initialValue); setError(null) }
  }, [open, initialValue])

  const handleConfirm = () => {
    const trimmed = value.trim()
    if (!trimmed) return
    const validationError = validate?.(trimmed) ?? null
    if (validationError) { setError(validationError); return }
    onConfirm(trimmed)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ 'data-testid': `${testIdPrefix}_dlg` } as any}>
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography component="span" variant="subtitle1" data-testid={`${testIdPrefix}_title`}
          sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
          {title}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
        <form onSubmit={(e) => { e.preventDefault(); handleConfirm() }}>
          {label && (
            <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.7, display: 'block', mb: 0.5 }}>
              {label}
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={placeholder}
            value={value}
            onChange={e => { setValue(e.target.value); if (error) setError(null) }}
            onKeyDown={e => {
              if (e.key === 'Enter' && singleLine && !e.shiftKey) {
                e.preventDefault()
                handleConfirm()
              }
            }}
            multiline={!singleLine}
            rows={singleLine ? undefined : 4}
            error={!!error}
            helperText={error ?? undefined}
            inputProps={{ 'data-testid': `${testIdPrefix}_input` }}
            FormHelperTextProps={{ 'data-testid': `${testIdPrefix}_error` } as any}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <FancyButton type="button" testId={`${testIdPrefix}_cancel_btn`} onClick={onClose}>{cancelLabel}</FancyButton>
            <FancyButton type="submit" solid testId={`${testIdPrefix}_ok_btn`} disabled={!value.trim()}>{okLabel}</FancyButton>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  )
}
